import { NotificationConfig, NotificationType } from '@routific/hive';
import i18n from '../../i18n';
import { PREDICTED_TRAFFIC_HELP_ARTICLE_URL } from '../../components/account/utils/constants';
import { ViolationType } from '../../store/mst/models/ViolationTypeEnum';

// Explains why some orders were left unscheduled when their time window can't fit their duration
// (the "schedule the whole order within its time window" preference). Empty when there are none.
export const buildTimeWindowShorterThanDurationMessage = (count?: number | null) => (
  count && count > 0
    ? i18n.t('notifications:optimizationSuccess.notification.unscheduledTimeWindowShorterThanDuration', { count })
    : ''
);

export const buildOutputMessage = (output?: any) => {
  if (!output?.details) {
    return i18n.t('notifications:optimizationSuccess.notification.defaultMessage');
  }

  const {
    unscheduledOrdersCount = 0,
    scheduledOrdersCount = 0,
    newScheduledOrdersCount = 0,
    unscheduledDueToTimeWindowShorterThanDuration = 0,
    time_of_day_eta_applied,
    ignored_features,
  } = output.details;

  const actualScheduledCount = scheduledOrdersCount || newScheduledOrdersCount || 0;
  const totalOrdersCount = actualScheduledCount + unscheduledOrdersCount;
  
  let message;
  if (unscheduledOrdersCount > 0) {
    message = i18n.t('notifications:optimizationSuccess.notification.scheduledWithTotal', {
      scheduled: actualScheduledCount,
      total: totalOrdersCount,
    });
  } else {
    message = i18n.t('notifications:optimizationSuccess.notification.scheduled', {
      count: actualScheduledCount,
    });
  }

  message += buildTimeWindowShorterThanDurationMessage(unscheduledDueToTimeWindowShorterThanDuration);

  if (!ignored_features) {
    return message;
  }

  const notOptimizedParts: string[] = [];
  if (time_of_day_eta_applied === false) {
    message += i18n.t('notifications:optimizationSuccess.notification.outOfRegion');
    notOptimizedParts.push(i18n.t('notifications:optimizationSuccess.features.historicalTraffic'));
  }

  // If capacities/loads was ignored
  if (ignored_features.has_capacities) {
    notOptimizedParts.push(i18n.t('notifications:optimizationSuccess.features.loads'));
  }

  // If balancing was ignored
  if (ignored_features.has_balance) {
    notOptimizedParts.push(i18n.t('notifications:optimizationSuccess.features.balancing'));
  }

  if (notOptimizedParts.length > 0) {
    let listStr = '';
    if (notOptimizedParts.length === 1) {
      listStr = notOptimizedParts[0];
    } else if (notOptimizedParts.length === 2) {
      listStr = notOptimizedParts.join(` ${i18n.t('common:and')} `);
    } else {
      // Join all but last with commas, then add “and last” for the final item
      listStr = 
        notOptimizedParts.slice(0, -1).join(', ') +
        ` ${i18n.t('common:and')} ` +
        notOptimizedParts[notOptimizedParts.length - 1];
    }

    message += i18n.t('notifications:optimizationSuccess.notification.notOptimized', {
      features: listStr,
    });
  }

  return message;
};

export const scheduleSuccessNotification = (actionUuid: string, output?: any): NotificationConfig => {
  const url = output?.details?.ignored_features ? PREDICTED_TRAFFIC_HELP_ARTICLE_URL  : undefined;

  return {
    type: NotificationType.SUCCESS,
    id: `${actionUuid}-scheduled`,
    message: buildOutputMessage(output),
    // Optional "linkMessage" that the notification might display as clickable text
    linkMessage: output?.details?.ignored_features ? i18n.t('notifications:optimizationSuccess.notification.link.title') : undefined,
    linkOnClick: url
      ? () => window.open(url, '_blank', 'noopener,noreferrer')
      : undefined,
    dataCy: 'schedule-success-notification',
  };
};

const getViolationMessage = (violations: string[], isInfeasibleInitialSolution: boolean) => {
  const hasCapacity = violations.includes(ViolationType.CAPACITY);
  const hasTimeWindows = violations.includes(ViolationType.TIME_WINDOWS);
  const hasOvertime = violations.includes(ViolationType.OVERTIME);
  const hasOutOfRegion = violations.includes(ViolationType.OUT_OF_REGION);
  const hasTags = violations.includes(ViolationType.TAGS);

  // Tags and out-of-region have standalone copy that isn't prefix-dependent, so surface them for
  // any engine error code that reports the violation (not just NO_SCHEDULED_ORDERS / INFEASIBLE).
  if (hasOutOfRegion) {
    return i18n.t('notifications:scheduleError.outOfRegion');
  }

  if (hasTags) {
    return i18n.t('notifications:scheduleError.tags');
  }

  // Capacity/time-window/overtime copy is framed by whether the conflict is with existing routes
  // ("existing") or the new orders being scheduled ("newOrders"). Default unknown codes to the
  // newOrders framing so a specific reason still surfaces instead of the generic fallback.
  const prefix = isInfeasibleInitialSolution ? 'existing' : 'newOrders';

  const parts = [
    hasCapacity && 'capacity',
    hasTimeWindows && 'timewindows',
    hasOvertime && 'overtime',
  ].filter(Boolean);

  if (parts.length === 0) {
    return null;
  }

  return i18n.t(`notifications:scheduleError.${prefix}.${parts.join('_')}`);
};

export const buildOutputError = (output: any) => {
  if (output?.details?.error) { // specific error code
    const { code, details } = output?.details?.error;

    // Check for specific violations in metadata regardless of the specific error code.
    if (details?.violations?.length > 0) {
      const isInfeasibleInitialSolution = code === 'INFEASIBLE_INITIAL_SOLUTION';
      const violationMessage = getViolationMessage(details.violations, isInfeasibleInitialSolution);
      if (violationMessage) {
        return violationMessage;
      }
    }
    return i18n.t('notifications:scheduleError.noScheduledOrders');
  }
  if (output?.error) {
    return output?.error;
  }
  return i18n.t('notifications:scheduleError.generic');
};

export const scheduleFailureNotification = (projectId: string, output?: any): NotificationConfig => ({
  type: NotificationType.ERROR,
  id: `${projectId}-schedule-failed`,
  message: buildOutputError(output),
  dataCy: 'schedule-failure-notification',
});

// Shown when the orders-page "Schedule all" action fails to resolve the set of
// not-scheduled orders to schedule (the uuid query rejected before any mutation).
export const scheduleAllFetchFailedNotification = (id: string): NotificationConfig => ({
  type: NotificationType.ERROR,
  id: `schedule-all-${id}-fetch-failed`,
  message: i18n.t('notifications:scheduleAll.fetchFailure'),
  dataCy: 'schedule-all-fetch-failure-notification',
});
